import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { getDesktopPreview, startComputerUse, VNC_PORT } from '@/api-models/computer-use';
import { useSandboxPorts } from '@/api-models/ports';
import { Button } from '@/ui/button/Button';
import { Spinner } from '@/ui/Spinner';

import { useDesktopSurface } from './desktop-surface';

/** Preview URLs are signed for the session's lifetime, so a remount can reuse
 * the one we already have instead of replaying start → wait-for-6080. */
const previewUrls = new Map<string, string>();

/** In-flight/settled `computer-use/start` calls, so a remount (or React's
 * double-invoked effects in dev) doesn't fire a second ten-second start. */
const starts = new Map<string, Promise<void>>();

function startOnce(base: string): Promise<void> {
  let pending = starts.get(base);
  if (!pending) {
    pending = startComputerUse(base).catch((e: unknown) => {
      starts.delete(base);
      throw e;
    });
    starts.set(base, pending);
  }
  return pending;
}

/**
 * Live desktop of the session's sandbox, embedded as the sandbox's own noVNC
 * client over its signed preview URL — cross-origin, so no VNC proxy here.
 *
 * The VNC stack is baked into the snapshot but never auto-starts, hence the
 * start → wait-for-6080 → preview-URL sequence before the iframe can load. The
 * iframe itself is owned by {@link useDesktopSurface}, not by this component, so
 * the connection survives unmounts.
 *
 * Note what is *not* here: a phase enum the async steps write into. `start` can
 * take ten seconds and resolves long after the port poll has already found 6080,
 * and a late `setPhase('waiting')` from that stale call is exactly what used to
 * blank a working desktop. Connected-ness is instead derived from `previewUrl`
 * alone, so nothing that finishes later can walk it back.
 */
export function DesktopTab({ base }: { base: string }) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(() => previewUrls.get(base) ?? null);
  const [startFailed, setStartFailed] = useState(false);
  const [startFinished, setStartFinished] = useState(false);
  // Only poll while we're still waiting for noVNC to come up: once it's streaming
  // the iframe talks to the sandbox directly, and further polling would be pure
  // background traffic against a sandbox the user may have let go idle.
  const { ports } = useSandboxPorts(previewUrl ? null : base);
  const vncListening = ports.includes(VNC_PORT);
  const slot = useRef<HTMLDivElement>(null);

  const src = previewUrl ? `${previewUrl}/vnc.html?autoconnect=true&resize=scale&reconnect=true` : null;
  useDesktopSurface(base, src, slot);

  const start = useCallback(async () => {
    setStartFailed(false);
    try {
      await startOnce(base);
      setStartFinished(true);
    } catch {
      toast.error('Failed to start the desktop');
      setStartFailed(true);
    }
  }, [base]);

  useEffect(() => {
    if (previewUrl) return;
    void start();
  }, [previewUrl, start]);

  useEffect(() => {
    if (!vncListening || previewUrl) return;
    let cancelled = false;
    getDesktopPreview(base)
      .then((preview) => {
        previewUrls.set(base, preview.url);
        if (!cancelled) setPreviewUrl(preview.url);
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to open the desktop');
      });
    return () => {
      cancelled = true;
    };
  }, [base, previewUrl, vncListening]);

  if (previewUrl) {
    // The desktop itself is drawn by the keep-alive surface tracking this box.
    return <div ref={slot} className="min-h-0 flex-1" />;
  }

  if (startFailed) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-sm text-fg-tertiary">The desktop could not be started.</p>
        <Button variant="secondary" size={7} onClick={() => void start()}>
          Start desktop
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 text-fg-tertiary">
      <Spinner size={6} />
      <p className="text-xs">{startFinished ? 'Waiting for the desktop…' : 'Starting the desktop…'}</p>
    </div>
  );
}
